import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Container } from '@mui/material';
import FMSToolbar from 'src/common/FMSToolbar';
import FilterSelectInput from 'src/common/FilterSelectInput';
import NewCardApproval from 'src/components/cards/approvals/NewCardApproval';
import CardApprovalsTable from 'src/components/cards/approvals/CardApprovalsTable';
import useAuth from 'src/context/AuthContext';
import ReportFilterModal from 'src/components/reports/ReportFilterModal';
import {
  limitRequestReportFormModel,
  limitRequestReportFormInitialValues,
  limitRequestReportValidationSchema
} from 'src/components/reports/limitRequestReportFormModal';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import DateFilterField from 'src/common/formFields/DateFilterField';
import API_URL from 'src/api/apiConstants';
import { DATE_FORMAT, USER_ROLES } from 'src/utils/constants';
import ScreenHeader from 'src/common/ScreenHeader';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';

const CardApprovals = ({ location }) => {
  const {
    search, site, date, setSearch, setSite, setDate
  } = usePersistentListFilters();
  const { getUser } = useAuth();
  const user = getUser();
  const isRequestor = user?.role === USER_ROLES.requestor;
  const isOperator = user?.role === USER_ROLES.operator;
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter(site);
  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState(search);
  const [searchFilters, setSearchFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [filterDate, setFilterDate] = useState(date || null);
  const downloadUrl = `${API_URL.cardLimitHistory}?export=true`;
  const filterOptions = {
    card: true, vehicle: true, vehicleGroup: true, requestStatus: true
  };
  const requestStatusOptions = [
    { id: 'pending', label: 'Pending' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
    { id: 'expired', label: 'Expired' },
    { id: 'cancelled', label: 'Cancelled' },
    { id: 'completed', label: 'Completed' },
  ];

  const handleSearch = (str) => { setSearchText(str); setSearch(str); };

  const handleExport = () => setOpenReportModal(true);
  const onReportModalClose = () => setOpenReportModal(false);

  const filterBydate = (newValue) => {
    const dateString = newValue ? newValue.format(DATE_FORMAT.unixDate) : undefined;
    setFilterDate(dateString);
    setDate(dateString || '');
  };

  const clearDateFilter = () => { setFilterDate(null); setDate(''); };

  return (
    <>
      <ScreenHeader title={isOperator ? 'Card Limit Requests' : 'Card Approvals'} />
      <Box
        className="transaction-style-page"
        sx={{
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <FMSToolbar
              className="card-approvals-toolbar"
              placeholder="Search Card/Vehicle"
              actionName="Export"
              actionIcon={<DownloadIcon />}
              onAction={handleExport}
              onSearch={handleSearch}
              searchValue={searchText}
              searchFields={[
                { key: 'card__uid', label: 'Card Number', defaultChecked: true },
                { key: 'vehicle__registration_num', label: 'Vehicle Number' },
                {
                  key: 'status', label: 'Request Status', type: 'select', options: requestStatusOptions
                }
              ]}
              searchValues={searchFilters}
              onSearchFields={(values) => { setSearchFilters(values); setSearchText(''); setSearch(''); }}
              onClearSearchFields={() => { setSearchFilters({}); setSearchText(''); setSearch(''); }}
              onRefresh={() => setRefreshKey((value) => value + 1)}
              hideAction={isOperator}
              hideActionDivider
              rightChildren={(
                <>
                  <DateFilterField
                    value={filterDate}
                    onDateChange={filterBydate}
                    onClearFilter={clearDateFilter}
                  />
                  {isAdmin && (
                    <FilterSelectInput
                      label="Site"
                      name="site"
                      size="small"
                      value={siteFilter}
                      data={siteOptions}
                      onInputChange={(value) => { setSiteFilter(value); setSite(value); }}
                      sx={{ width: { xs: '100%', sm: 220, md: 180 }, mr: 0 }}
                    />
                  )}
                  {isRequestor && (
                  <Button
                    color="primary"
                    size="small"
                    variant="contained"
                    onClick={() => setOpenModal(true)}
                    sx={{ height: 40, whiteSpace: 'nowrap' }}
                  >
                    Request Card Limit
                  </Button>
                  )}
                </>
              )}
              sx={{
                border: 'none',
                borderRadius: 0,
                background: 'transparent',
                '& .MuiCardContent-root': {
                  px: 0,
                  py: 1.5,
                  '&:last-child': { pb: 1.5 }
                },
                '& .MuiCardContent-root > .MuiStack-root': {
                  alignItems: 'flex-start'
                }
              }}
              actionButtonSize="small"
              actionButtonSx={{
                height: 40,
                minWidth: 145,
                px: 2,
                py: 1,
                fontSize: 14
              }}
            />
          </Box>
          <ReportFilterModal
            open={openReportModal}
            title="Card Limit Requests Report"
            filterConfig={filterOptions}
            formModel={limitRequestReportFormModel}
            formValidation={limitRequestReportValidationSchema}
            formInitValues={limitRequestReportFormInitialValues}
            downloadUrl={downloadUrl}
            handleClose={onReportModalClose}
          />
          <NewCardApproval open={openModal} handleClose={() => setOpenModal(false)} />
          <Box
            sx={{
              pt: 3,
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <CardApprovalsTable
              refreshData={refreshKey || location.state?.fromDashboard}
              searchText={searchText}
              searchFilters={searchFilters}
              dateFilter={filterDate}
              siteFilter={siteFilter}
            />
          </Box>
        </Container>
      </Box>
    </>
  );
};

CardApprovals.propTypes = {
  location: PropTypes.object
};

export default CardApprovals;
